import React, { useState, useRef } from 'react';
import { API } from './api';
import { toast } from 'sonner';
import { Upload, FileText, CheckCircle, AlertCircle, Users, Download, ArrowRight, Loader2, Calendar, Receipt, FileSpreadsheet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const DATA_TYPES = [
  { value: 'employees', label: 'Employees', icon: Users, description: 'Import employee master data' },
  { value: 'leave', label: 'Leave Records', icon: Calendar, description: 'Import leave history' },
  { value: 'claims', label: 'Claims', icon: Receipt, description: 'Import expense claims' },
];

export default function ImportData() {
  const [file, setFile] = useState(null);
  const [dataType, setDataType] = useState('employees');
  const [importing, setImporting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [step, setStep] = useState(1);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const validExtensions = ['.csv', '.xlsx', '.xls'];
      const hasValidExtension = validExtensions.some(ext => selectedFile.name.toLowerCase().endsWith(ext));
      
      if (!hasValidExtension) {
        toast.error('Please select a CSV or Excel file (.csv, .xlsx, .xls)');
        return;
      }
      setFile(selectedFile);
      await previewData(selectedFile);
    }
  };

  const previewData = async (file) => {
    setPreviewing(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await API.post(`/import/preview/${dataType}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setPreview(response.data);
      setStep(2);
      
      if (response.data.errors?.length > 0) {
        toast.warning(`Found ${response.data.errors.length} rows with issues`);
      }
    } catch (error) {
      console.error('Preview error:', error);
      toast.error(error.response?.data?.detail || 'Failed to preview file');
      setFile(null);
    } finally {
      setPreviewing(false);
    }
  };

  const handleImport = async () => {
    if (!file) return;
    
    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await API.post(`/import/execute/${dataType}?skip_duplicates=true`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setImportResult(response.data);
      setStep(3);
      toast.success(response.data.message);
    } catch (error) {
      console.error('Import error:', error);
      toast.error(error.response?.data?.detail || 'Failed to import data');
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const response = await API.get(`/import/template/${dataType}`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${dataType}_import_template.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const resetImport = () => {
    setFile(null);
    setPreview(null);
    setImportResult(null);
    setStep(1);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const currentType = DATA_TYPES.find(t => t.value === dataType);
  const TypeIcon = currentType?.icon || FileText;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Import Data</h2>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            Import data from Kakitangan.com or other HR systems
          </p>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center justify-center space-x-4 mb-8">
        {[
          { num: 1, label: 'Select & Upload' },
          { num: 2, label: 'Preview Data' },
          { num: 3, label: 'Import Complete' }
        ].map((s, i) => (
          <React.Fragment key={s.num}>
            <div className={`flex items-center space-x-2 ${step >= s.num ? 'text-emerald-600' : 'text-slate-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                step >= s.num ? 'bg-emerald-600 text-white' : 'bg-slate-200 dark:bg-slate-700'
              }`}>
                {step > s.num ? <CheckCircle className="h-5 w-5" /> : s.num}
              </div>
              <span className="hidden sm:inline font-medium">{s.label}</span>
            </div>
            {i < 2 && <ArrowRight className="h-5 w-5 text-slate-300" />}
          </React.Fragment>
        ))}
      </div>

      {/* Step 1: Upload */}
      {step === 1 && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-8">
          <div className="text-center">
            <div className="mx-auto w-16 h-16 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center mb-4">
              <Upload className="h-8 w-8 text-emerald-600" />
            </div>
            <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">
              Import from Kakitangan
            </h3>
            <p className="text-slate-600 dark:text-slate-400 mb-6">
              Export your data from Kakitangan.com and upload it here
            </p>

            {/* Data Type Selection */}
            <div className="max-w-md mx-auto mb-6">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 text-left">
                What would you like to import?
              </label>
              <Select value={dataType} onValueChange={setDataType}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DATA_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      <div className="flex items-center">
                        <type.icon className="h-4 w-4 mr-2 text-slate-500" />
                        <span>{type.label}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-sm text-slate-500 mt-1 text-left">{currentType?.description}</p>
            </div>

            <div className="border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-xl p-8 hover:border-emerald-500 transition-colors">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileSelect}
                className="hidden"
                id="file-upload"
                disabled={previewing}
              />
              <label htmlFor="file-upload" className={`cursor-pointer ${previewing ? 'opacity-50' : ''}`}>
                {previewing ? (
                  <Loader2 className="h-12 w-12 text-emerald-500 mx-auto mb-4 animate-spin" />
                ) : (
                  <FileSpreadsheet className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                )}
                <p className="text-slate-600 dark:text-slate-400 mb-2">
                  {previewing ? 'Processing file...' : (
                    <><span className="text-emerald-600 font-semibold">Click to upload</span> or drag and drop</>
                  )}
                </p>
                <p className="text-sm text-slate-500">CSV or Excel file (.csv, .xlsx, .xls)</p>
              </label>
            </div>

            <div className="mt-6 flex items-center justify-center space-x-4">
              <Button variant="outline" onClick={downloadTemplate}>
                <Download className="h-4 w-4 mr-2" />
                Download Template
              </Button>
            </div>

            <div className="mt-8 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-left">
              <h4 className="font-semibold text-blue-900 dark:text-blue-300 mb-2">How to export from Kakitangan:</h4>
              <ol className="text-sm text-blue-800 dark:text-blue-400 space-y-1 list-decimal list-inside">
                <li>Log in to your Kakitangan.com account</li>
                <li>Go to <strong>{dataType === 'employees' ? 'Employees → All Employees' : dataType === 'leave' ? 'Leave → Leave History' : 'Claims → All Claims'}</strong></li>
                <li>Click <strong>Export</strong> button</li>
                <li>Select <strong>CSV</strong> or <strong>Excel</strong> format</li>
                <li>Upload the downloaded file here</li>
              </ol>
            </div>
          </div>
        </div>
      )}

      {/* Step 2: Preview */}
      {step === 2 && preview && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
                Preview Import Data
              </h3>
              <p className="text-slate-600 dark:text-slate-400">
                Found <span className="font-semibold text-emerald-600">{preview.valid_records}</span> valid records to import
              </p>
            </div>
            <Button variant="outline" onClick={resetImport}>
              Upload Different File
            </Button>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-slate-50 dark:bg-slate-900/50 rounded-lg p-4">
              <FileText className="h-6 w-6 text-slate-600 mb-2" />
              <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{preview.total_rows}</p>
              <p className="text-sm text-slate-600 dark:text-slate-400">Total Rows</p>
            </div>
            <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-4">
              <CheckCircle className="h-6 w-6 text-emerald-600 mb-2" />
              <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-400">{preview.valid_records}</p>
              <p className="text-sm text-emerald-600 dark:text-emerald-500">Valid Records</p>
            </div>
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
              <TypeIcon className="h-6 w-6 text-blue-600 mb-2" />
              <p className="text-2xl font-bold text-blue-700 dark:text-blue-400">{preview.columns_mapped?.length || 0}</p>
              <p className="text-sm text-blue-600 dark:text-blue-500">Fields Mapped</p>
            </div>
            {preview.errors?.length > 0 && (
              <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4">
                <AlertCircle className="h-6 w-6 text-amber-600 mb-2" />
                <p className="text-2xl font-bold text-amber-700 dark:text-amber-400">{preview.errors.length}</p>
                <p className="text-sm text-amber-600 dark:text-amber-500">Rows with Issues</p>
              </div>
            )}
          </div>

          {/* Preview Table */}
          <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden mb-6">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 dark:bg-slate-900">
                  <tr>
                    {dataType === 'employees' && (
                      <>
                        <th className="px-4 py-3 text-left font-semibold text-slate-900 dark:text-slate-100">Employee ID</th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-900 dark:text-slate-100">Name</th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-900 dark:text-slate-100">Email</th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-900 dark:text-slate-100">Department</th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-900 dark:text-slate-100">Position</th>
                      </>
                    )}
                    {dataType === 'leave' && (
                      <>
                        <th className="px-4 py-3 text-left font-semibold text-slate-900 dark:text-slate-100">Employee ID</th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-900 dark:text-slate-100">Leave Type</th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-900 dark:text-slate-100">Start Date</th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-900 dark:text-slate-100">End Date</th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-900 dark:text-slate-100">Days</th>
                      </>
                    )}
                    {dataType === 'claims' && (
                      <>
                        <th className="px-4 py-3 text-left font-semibold text-slate-900 dark:text-slate-100">Employee</th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-900 dark:text-slate-100">Type</th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-900 dark:text-slate-100">Amount</th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-900 dark:text-slate-100">Date</th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-900 dark:text-slate-100">Status</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                  {preview.preview?.map((record, index) => (
                    <tr key={index} className="hover:bg-slate-50 dark:hover:bg-slate-700/50">
                      {dataType === 'employees' && (
                        <>
                          <td className="px-4 py-3 text-slate-900 dark:text-slate-100">{record.employee_id}</td>
                          <td className="px-4 py-3 text-slate-900 dark:text-slate-100 font-medium">{record.full_name}</td>
                          <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{record.email}</td>
                          <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{record.department}</td>
                          <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{record.position}</td>
                        </>
                      )}
                      {dataType === 'leave' && (
                        <>
                          <td className="px-4 py-3 text-slate-900 dark:text-slate-100">{record.employee_id}</td>
                          <td className="px-4 py-3 text-slate-900 dark:text-slate-100 capitalize">{record.leave_type}</td>
                          <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{record.start_date?.split('T')[0]}</td>
                          <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{record.end_date?.split('T')[0]}</td>
                          <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{record.days_taken}</td>
                        </>
                      )}
                      {dataType === 'claims' && (
                        <>
                          <td className="px-4 py-3 text-slate-900 dark:text-slate-100">{record.employee_name || record.employee_id}</td>
                          <td className="px-4 py-3 text-slate-900 dark:text-slate-100 capitalize">{record.claim_type}</td>
                          <td className="px-4 py-3 text-slate-600 dark:text-slate-400">RM {record.amount?.toLocaleString()}</td>
                          <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{record.submitted_date?.split('T')[0]}</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              record.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                              record.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {record.status}
                            </span>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {preview.valid_records > 10 && (
              <div className="px-4 py-3 bg-slate-50 dark:bg-slate-900 text-center text-sm text-slate-500">
                ... and {preview.valid_records - 10} more records
              </div>
            )}
          </div>

          {/* Errors if any */}
          {preview.errors?.length > 0 && (
            <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4 mb-6">
              <h4 className="font-semibold text-amber-900 dark:text-amber-300 mb-2 flex items-center">
                <AlertCircle className="h-5 w-5 mr-2" />
                Rows with Issues (will be skipped)
              </h4>
              <ul className="text-sm text-amber-800 dark:text-amber-400 space-y-1">
                {preview.errors.slice(0, 5).map((err, i) => (
                  <li key={i}>Row {err.row}: {err.error}</li>
                ))}
                {preview.errors.length > 5 && (
                  <li>... and {preview.errors.length - 5} more issues</li>
                )}
              </ul>
            </div>
          )}

          {/* Import Button */}
          <div className="flex items-center justify-end space-x-4">
            <Button variant="outline" onClick={resetImport}>
              Cancel
            </Button>
            <Button 
              onClick={handleImport} 
              disabled={importing || preview.valid_records === 0}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {importing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Import {preview.valid_records} Records
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Step 3: Result */}
      {step === 3 && importResult && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-8">
          <div className="text-center">
            <div className="mx-auto w-16 h-16 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="h-8 w-8 text-emerald-600" />
            </div>
            <h3 className="text-2xl font-semibold text-slate-900 dark:text-slate-100 mb-2">
              Import Complete!
            </h3>
            <p className="text-slate-600 dark:text-slate-400 mb-6">
              {importResult.message}
            </p>

            {/* Result Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-4">
                <p className="text-3xl font-bold text-emerald-700 dark:text-emerald-400">{importResult.imported}</p>
                <p className="text-sm text-emerald-600">Records Imported</p>
              </div>
              <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4">
                <p className="text-3xl font-bold text-amber-700 dark:text-amber-400">{importResult.skipped || 0}</p>
                <p className="text-sm text-amber-600">Skipped (Duplicates)</p>
              </div>
              <div className="bg-slate-50 dark:bg-slate-900/50 rounded-lg p-4">
                <p className="text-3xl font-bold text-slate-700 dark:text-slate-300">{importResult.total_processed}</p>
                <p className="text-sm text-slate-600">Total Processed</p>
              </div>
            </div>

            {/* Errors if any */}
            {importResult.errors?.length > 0 && (
              <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4 mb-6 text-left">
                <h4 className="font-semibold text-red-900 dark:text-red-300 mb-2 flex items-center">
                  <AlertCircle className="h-5 w-5 mr-2" />
                  Import Errors
                </h4>
                <ul className="text-sm text-red-800 dark:text-red-400 space-y-1">
                  {importResult.errors.slice(0, 5).map((err, i) => (
                    <li key={i}>Row {err.row}: {err.error}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex items-center justify-center space-x-4">
              <Button variant="outline" onClick={resetImport}>
                Import More Data
              </Button>
              <Button 
                onClick={() => window.location.href = `/suite/hr/${dataType === 'employees' ? 'employees' : dataType === 'leave' ? 'leave' : 'claims'}`}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                <TypeIcon className="h-4 w-4 mr-2" />
                View {currentType?.label}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
